import React from 'react';

// ============================================================
// Bespoke XION icon set — hand-crafted stroke SVGs in the neon
// #00FFD1 aesthetic. Each icon inherits `currentColor` and carries
// its own ambient ("static") animation via classes defined in index.css.
// ============================================================

const Base = ({ size = 24, className = '', strokeWidth = 1.6, children, ...rest }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 32 32"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`xi ${className}`}
    aria-hidden="true"
    focusable="false"
    {...rest}
  >
    {children}
  </svg>
);

/* 4K / UHD — focus reticle on a monitor with a live scan sweep */
export const FourK = (p) => (
  <Base {...p}>
    <rect x="3.5" y="5.5" width="25" height="17" rx="2" />
    <path d="M16 22.5v4M11.5 26.5h9" />
    <path d="M8 11.5V9.5h2M22 9.5h2v2M24 16.5v2h-2M10 18.5H8v-2" />
    <line className="xi-scan" x1="9" y1="14" x2="23" y2="14" />
    <circle cx="16" cy="14" r="1.4" />
  </Base>
);

/* True Full 3D — isometric cube with the near edges highlighted */
export const TrueFull3D = (p) => (
  <Base {...p}>
    <path d="M16 4.5 24.5 9.5 24.5 20.5 16 25.5 7.5 20.5 7.5 9.5 Z" />
    <path d="M16 15 16 4.5M16 15 24.5 20.5M16 15 7.5 20.5" />
  </Base>
);

/* PIET imaging — camera iris with a rotating dashed lens ring + sparkle */
export const PietImaging = (p) => (
  <Base {...p}>
    <circle className="xi-dash" cx="16" cy="16" r="10.5" />
    <circle cx="16" cy="16" r="5.5" />
    <g className="xi-spin">
      <path d="M16 10.5 21 18.5M16 21.5 11 13.5M21.6 15 12.4 17" />
    </g>
    <path fill="currentColor" stroke="none" d="M26.5 4.5 27.3 6.6 29.4 7.4 27.3 8.2 26.5 10.3 25.7 8.2 23.6 7.4 25.7 6.6 Z" />
  </Base>
);

/* EGG module — live electroglottography waveform */
export const EggModule = (p) => (
  <Base {...p}>
    <path className="xi-live" d="M3 16h4l2.5-8 3 14 3-11 2.5 7h3l2-4h5" />
  </Base>
);

/* ENT — outer ear with pulsing sound arcs */
export const EntIcon = (p) => (
  <Base {...p}>
    <path d="M11.5 27c-3.5-2-5.5-5.5-5.5-10a8 8 0 1 1 16 0c0 3.5-2.5 4.8-4.5 5.8-1.6.8-2 2-1 3.7" />
    <path d="M12 14.5a4 4 0 1 1 6.5 3" />
    <path className="xi-wave" d="M23 12a6 6 0 0 1 0 8" />
    <path className="xi-wave" style={{ animationDelay: '.35s' }} d="M26 9a10.5 10.5 0 0 1 0 14" />
  </Base>
);

/* Gynaecology — Venus symbol */
export const GynecologyIcon = (p) => (
  <Base {...p}>
    <circle cx="16" cy="11" r="6.5" />
    <path d="M16 17.5v9M11.5 23h9" />
  </Base>
);

/* Laparoscopy — minimally-invasive triangulation into the abdomen */
export const LaparoscopyIcon = (p) => (
  <Base {...p}>
    <path d="M4 25q12-7 24 0" />
    <line x1="8.5" y1="5" x2="15" y2="20" />
    <line x1="23.5" y1="5" x2="17" y2="20" />
    <line x1="16" y1="4" x2="16" y2="19.5" />
    <circle cx="16" cy="20.5" r="1.6" />
  </Base>
);

/* Urology — droplet with an inner highlight */
export const UrologyIcon = (p) => (
  <Base {...p}>
    <path d="M16 4c5 7 8 10 8 14a8 8 0 0 1-16 0c0-4 3-7 8-14Z" />
    <path d="M12 18.5a4 4 0 0 0 1 4.5" />
  </Base>
);

/* Office / clinic — compact building with a medical cross */
export const OfficeIcon = (p) => (
  <Base {...p}>
    <path d="M6 27V13l10-6 10 6v14" />
    <path d="M4.5 27h23" />
    <path d="M13 27v-5h6v5" />
    <path d="M16 10v3M14.5 11.5h3" />
  </Base>
);

/* Hospital — building block with roof cross + windows */
export const HospitalIcon = (p) => (
  <Base {...p}>
    <rect x="6" y="10.5" width="20" height="16.5" rx="1.5" />
    <path d="M4.5 27h23" />
    <path d="M16 3v5M13.5 5.5h5" />
    <path d="M10 15h3M19 15h3M10 19h3M19 19h3" />
    <path d="M14 27v-4.5h4V27" />
  </Base>
);

/* PIET lumino — brightness / sun with pulsing rays */
export const LuminoIcon = (p) => (
  <Base {...p}>
    <circle cx="16" cy="16" r="6" />
    <g className="xi-rays">
      <path d="M16 3v3.5M16 25.5V29M3 16h3.5M25.5 16H29M6.7 6.7l2.5 2.5M22.8 22.8l2.5 2.5M25.3 6.7l-2.5 2.5M9.2 22.8l-2.5 2.5" />
    </g>
  </Base>
);

/* PIET chromo — colour contrast (split disc) */
export const ChromoIcon = (p) => (
  <Base {...p}>
    <circle cx="16" cy="16" r="10.5" />
    <path fill="currentColor" stroke="none" d="M16 5.5a10.5 10.5 0 0 1 0 21Z" />
  </Base>
);

/* PIET spectro — prism dispersing the spectrum */
export const SpectroIcon = (p) => (
  <Base {...p}>
    <path d="M15 6 24.5 22 12 22 Z" />
    <path d="M3.5 14.5 13.5 16.5" />
    <g className="xi-spectrum">
      <path d="M22 19.5 29 16.5" />
      <path d="M22.5 21 29.5 19.5" />
      <path d="M22.5 22.5 29.5 23" />
      <path d="M22 24 28.5 26.5" />
    </g>
  </Base>
);

export const medicalFieldIcons = {
  ent: EntIcon,
  gynecology: GynecologyIcon,
  laparoscopy: LaparoscopyIcon,
  urology: UrologyIcon,
};
